import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BadgeEuro, Building2, Scale, FileSearch, Calculator, Handshake, Check, ShieldCheck } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1651156956850-5195ed474c18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBnbGFzcyUyMGJ1aWxkaW5nJTIwYXJjaGl0ZWN0dXJlJTIwbmlnaHQlMjBza3lsaW5lfGVufDB8fHxibHVlfDE3ODgyOTkxOTl8MA&ixlib=rb-4.1.0&q=85";

const pillars = [
  {
    icon: BadgeEuro,
    title: "CEE",
    text: "Nous identifions les opérations éligibles, montons vos dossiers et valorisons vos CEE auprès des obligataires.",
    testid: "pillar-card-cee",
  },
  {
    icon: Building2,
    title: "Rénovation énergétique",
    text: "De l'audit assuré par un bureau d'études au suivi de chantier. Nous construisons le scénario le plus adapté à votre situation et à vos besoins.",
    testid: "pillar-card-renovation",
  },
  {
    icon: Scale,
    title: "Décret Tertiaire 2030",
    text: "Mise en conformité avec le dispositif Éco-Énergie Tertiaire pour les années 2030, 2040, 2050.",
    testid: "pillar-card-decret",
  },
];

const stats = [
  { value: "100 %", label: "Financés par les Certificats d'Économies d'Énergie" },
  { value: "A → Z", label: "Accompagnement complet" },
  { value: "RGE", label: "Partenaires certifiés" },
  { value: "2030", label: "Horizon Décret Tertiaire" },
];

const steps = [
  { num: "01", icon: FileSearch, title: "Audit & diagnostic", text: "Analyse complète des consommations, de vos bâtiments et obligations réglementaires." },
  { num: "02", icon: Calculator, title: "Ingénierie financière", text: "Chiffrage des scénarios, mobilisation des CEE et des aides disponibles pour financer vos travaux." },
  { num: "03", icon: Handshake, title: "Sélection de partenaires", text: "Mise en relation avec notre réseau de bureaux d'études et installateurs RGE." },
];

const partners = [
  "Bureaux d'études thermiques",
  "Installeurs certifiés RGE",
  "Obligataires & financeurs CEE",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/85 to-navy-950" />
          <div className="absolute inset-0 hero-glow" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32 grid lg:grid-cols-[1.3fr_1fr] gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-gold mb-6">
              Conseil • Étude • Accompagnement
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-[1.15] text-white">
              Faites de votre transition énergétique un <span className="gold-text">levier de valeur</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300 font-light max-w-xl">
              Levi One conseille et accompagne les entreprises dans leurs projets de rénovation
              énergétique en conformité avec le Décret Tertiaire 2030.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-submit-audit-btn"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-gold to-gold-bright hover:from-gold-bright hover:to-gold text-navy-950 font-semibold px-7 py-3.5 rounded-full shadow-lg shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5"
              >
                Demander une étude <ArrowRight size={18} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-discover-services-btn"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold-bright hover:bg-gold/10 font-medium px-7 py-3.5 rounded-full transition-colors duration-300"
              >
                Découvrir nos services
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="hidden lg:flex justify-center"
          >
            <div className="relative" data-testid="hero-logo">
              <div className="absolute -inset-4 rounded-full border border-dashed border-gold/40 animate-spin-slow" />
              <div className="absolute -inset-10 rounded-full border border-gold/10" />
              <img src="/logo.png" alt="Logo Levi One" className="w-64 h-64 rounded-full gold-ring-glow" />
            </div>
          </motion.div>
        </div>

        <div className="relative border-t border-gold/15 bg-navy-950/60 backdrop-blur">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="hero-stats">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.1, duration: 0.5 }}
              >
                <p className="text-2xl sm:text-3xl font-serif font-bold gold-text">{s.value}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24" data-testid="pillars-section">
        <SectionHeading
          overline="Nos expertises"
          title="Trois piliers pour votre performance énergétique"
          text="Une approche de conseil globale, de la réglementation au financement des travaux."
        />
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <Link
                to="/services"
                data-testid={p.testid}
                className="block h-full bg-navy-800/60 border border-gold/15 rounded-2xl p-8 hover:border-gold/50 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-gold/10 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
                  <p.icon size={22} className="text-gold-bright" />
                </div>
                <h3 className="text-xl font-sans font-medium tracking-wide text-gold-bright mb-3">{p.title}</h3>
                <p className="text-sm leading-relaxed text-slate-400">{p.text}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-gold group-hover:gap-3 transition-all">
                  En savoir plus <ArrowRight size={15} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-navy-900 border-y border-gold/10" data-testid="defense-section">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-24">
          <Reveal>
            <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mb-6">
              <ShieldCheck size={26} className="text-gold-bright" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold mb-3">Notre engagement</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight text-white leading-snug">
              Face aux obligataires CEE et aux installateurs, <span className="gold-text">nous défendons vos intérêts</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-400">
              Une prime CEE se négocie, et son montant varie fortement d'un obligataire à l'autre :
              notre rôle est de les mettre en concurrence avec les entreprises qui réaliseront
              les travaux.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy-900 border-y border-gold/10" data-testid="methodology-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
          <SectionHeading
            overline="Notre méthode"
            title="Un accompagnement personnalisé"
            text="Présents à chaque étape, nous vous accompagnons jusqu'à la mise en service de votre installation."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl">
            {steps.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div className="relative bg-navy-800/50 border border-gold/10 rounded-2xl p-7 h-full hover:border-gold/40 transition-colors duration-300" data-testid={`method-step-${s.num}`}>
                  <span className="absolute top-5 right-6 text-4xl font-serif font-bold text-gold/15">{s.num}</span>
                  <s.icon size={24} className="text-gold mb-5" />
                  <h4 className="text-lg font-medium text-white mb-2">{s.title}</h4>
                  <p className="text-sm leading-relaxed text-slate-400">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center" data-testid="partners-section">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold mb-3">Écosystème</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight text-white leading-snug">
            Des partenaires de qualité, rigoureusement sélectionnés
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-400">
            Chaque partenaire de notre réseau est évalué selon une charte d'exigence :
            certifications, références, solidité financière et qualité d'exécution.
          </p>
          <ul className="mt-8 space-y-4">
            {partners.map((p) => (
              <li key={p} className="flex items-center gap-3 text-slate-300">
                <span className="w-6 h-6 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0">
                  <Check size={13} className="text-gold-bright" />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative rounded-2xl overflow-hidden border border-gold/20 shadow-2xl shadow-black/40">
            <img
              src="/pac-exterieure.jpg"
              alt="Pompes à chaleur industrielles installées chez un client Levi One"
              className="w-full h-[420px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 to-transparent" />
            <p className="absolute bottom-6 left-6 right-6 text-sm text-slate-300">
              Un réseau d'experts mobilisé sur vos projets, du bureau d'études au chantier.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24" data-testid="home-cta-section">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-gold/25 bg-gradient-to-br from-navy-800 to-navy-950 px-8 sm:px-16 py-16 text-center">
            <div className="absolute inset-0 hero-glow" />
            <div className="relative">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold text-white">
                Prêt à engager votre transition énergétique ?
              </h2>
              <p className="mt-4 text-slate-400 max-w-xl mx-auto">
                Bénéficiez d'un premier échange sans engagement pour évaluer votre potentiel
                d'économies et vos obligations réglementaires.
              </p>
              <Link
                to="/contact"
                data-testid="home-cta-contact-btn"
                className="mt-8 inline-flex items-center gap-2 bg-gradient-to-r from-gold to-gold-bright hover:from-gold-bright hover:to-gold text-navy-950 font-semibold px-8 py-4 rounded-full shadow-lg shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5"
              >
                Échanger avec un conseiller <ArrowRight size={18} />
              </Link>
              <Link
                to="/fiches-cee"
                data-testid="home-cta-simulator-btn"
                className="mt-8 ml-4 inline-flex items-center gap-2 border border-gold/40 text-gold-bright hover:bg-gold/10 font-medium px-8 py-4 rounded-full transition-colors duration-300"
              >
                Découvrir nos fiches CEE
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
